import { useEffect, useId, useState } from 'react';
import { type ProviderItem, type TierFDetail, fetchTierFDetail } from '../lib/api.js';
import CommandPanel from './ui/CommandPanel.js';
import IconButton from './ui/IconButton.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

// TierFCard v0.2.1 — collapsible with persisted visitor state.
// Bottom-of-page card listing providers reclassified
// out of the main grid because they misrepresent reality (status dashboard
// taken offline mid-outage, stuck on "all green" while broken, etc.). The
// F grade carries a 1-year minimum lock-in enforced server-side. Clicking
// a row opens TierFDetailModal which loads the timestamped evidence log
// for that provider — operator-written notes, optionally with outbound
// links to news stories or post-mortems. The data is intentionally
// public-readable (anyone can see why we graded a provider F) but
// operator-write only.

interface Props {
  providers: ProviderItem[];
}

export default function TierFCard({ providers }: Props) {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [expanded, setExpanded] = usePersistedPanelState('panel:tier-f', true);
  const contentId = useId();
  if (!providers || providers.length === 0) return null;
  return (
    <CommandPanel className="border border-bad/45">
      <SectionHeader
        title={<span className="text-bad">TIER F · UNTRUSTWORTHY</span>}
        right={
          <span className="text-dim">
            {providers.length} provider{providers.length === 1 ? '' : 's'}
          </span>
        }
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <>
          <p className="px-3 py-2 text-xs text-muted font-mono">
            Providers graded F have demonstrably misrepresented status service behavior, are held out
            of active monitoring surfaces, and carry a one-year minimum lock-in.
          </p>
          <ul className="divide-y divide-border/40">
            {providers.map((p) => (
              <li key={p.slug}>
                <button
                  type="button"
                  onClick={() => setOpenSlug(p.slug)}
                  className="w-full text-left px-3 py-2 hover:bg-surface2 flex items-center gap-2 font-mono text-sm"
                >
                  <StatusGlow severity={3}>F</StatusGlow>
                  <span className="text-text truncate">{p.name}</span>
                  {p.reputation_summary && (
                    <span className="text-muted text-xs truncate flex-1">
                      · {p.reputation_summary}
                    </span>
                  )}
                  <span className="ml-auto text-dim text-xs shrink-0">view evidence ↗</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {openSlug && <TierFDetailModal slug={openSlug} onClose={() => setOpenSlug(null)} />}
    </CommandPanel>
  );
}

interface ModalProps {
  slug: string;
  onClose: () => void;
}

function TierFDetailModal({ slug, onClose }: ModalProps) {
  const [detail, setDetail] = useState<TierFDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTierFDetail(slug)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e?.message ?? e));
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handled via document keydown
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative API
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only — backdrop swallows clicks */}
      <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] overflow-y-auto">
        <CommandPanel className="border border-bad/45 max-w-2xl w-full" bodyClassName="p-0">
          <SectionHeader
            title={<span className="text-bad">⚠ TIER F · {detail?.name ?? slug}</span>}
            right={
              <IconButton as="button" label="Close" stopPropagation onClick={onClose}>
                ×
              </IconButton>
            }
          />
          <div className="p-3 pt-2 space-y-3 font-mono text-sm">
            {detail?.summary && <p className="text-xs text-muted">{detail.summary}</p>}
            <div className="text-[11px] text-muted/70 italic leading-relaxed border-l-2 border-bad/40 pl-3">
              The grade and the notes below are the site operator&apos;s stated opinion, not a
              factual claim about the provider. Linked stories are third-party reporting; the
              operator does not vouch for their accuracy or endorse their conclusions. The grade is
              subject to change.
            </div>
            {detail && (
              <div className="text-xs text-muted flex flex-wrap gap-x-4 gap-y-1">
                {detail.set_at && (
                  <span>
                    Graded F: <span className="text-text">{fmtDate(detail.set_at)}</span>
                  </span>
                )}
                {detail.locked_until && (
                  <span>
                    Locked until: <span className="text-text">{fmtDate(detail.locked_until)}</span>
                  </span>
                )}
              </div>
            )}
            {error && <p className="text-bad text-xs">load failed: {error}</p>}
            {!detail && !error && <p className="text-dim text-xs">loading…</p>}
            {detail && detail.notes.length === 0 && (
              <p className="text-dim text-xs italic">no evidence notes recorded yet.</p>
            )}
            {detail && detail.notes.length > 0 && (
              <ol className="space-y-3">
                {detail.notes.map((n) => (
                  <li
                    key={n.id}
                    className="border-l-2 border-bad pl-3 py-1 bg-surface/40 rounded-sm"
                  >
                    <div className="text-xs text-dim mb-1">{fmtDate(n.created_at)}</div>
                    <div className="text-text whitespace-pre-line text-sm leading-relaxed">
                      {n.body}
                    </div>
                    {n.link_url && (
                      <a
                        href={n.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-accent text-xs hover:underline"
                      >
                        {n.link_label || n.link_url} ↗
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </CommandPanel>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  // SQL datetime('now') values are stored without a Z suffix; normalise so
  // toLocaleString interprets them as UTC, not local. Mirrors the api-worker
  // toIsoUtc helper rather than re-inventing UTC parsing here.
  const norm = iso.endsWith('Z') || iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`;
  try {
    return new Date(norm).toLocaleString();
  } catch {
    return iso;
  }
}
